import { call, put, takeEvery, select } from 'redux-saga/effects';
import { getType } from 'typesafe-actions';
import { RootState } from '../reducers/rootReducer';
import { setLoading } from '../actions/loading.actions';
import { callAPI } from '../../utils';
import { handleApiErrors, catchApiErrors } from './utils';
import {
  SAGA_RESET_PICK_TOUR,
  BFF_V1,
  BFF_PICK_TOURS,
  RESET_PICK_TOUR_ERROR,
  PICKING_APIGEE_BASE_PATH,
  GENERIC_NON_SERVICE_ERROR,
  PICKTOUR_ACTIVE,
  PICKTOUR_CLOSED,
} from '../../constants';
import { APIError, ConfigAuthValues } from '../../types';
import { APIPickTour } from './types';
import { resetActivePickTour, resetActivePickTourComplete, getPickToursInProgress } from '../actions/orders.actions';
import { createToast } from '../actions/toasts.actions';
import i18n from '../../i18n';
import { selectPickerStoreID } from '../selectors';

const callResetPickTour = (data: APIPickTour, configAuthValues: ConfigAuthValues) => {
  return callAPI({
    method: 'POST',
    url: `${configAuthValues.APIGEE_BASE_URL}${PICKING_APIGEE_BASE_PATH}${BFF_V1}${BFF_PICK_TOURS}/${data.pickTourID}/reassign?resetItems=false`,
    data,
  });
};

export function* runResetPickTourSaga(action: ReturnType<typeof resetActivePickTour>): Generator<any, void, any> {
  const configAuthValues: ConfigAuthValues = yield select((state: RootState) => state.config.authValues);
  const { pickTour, pickTourIndex } = action.payload;

  yield put(setLoading({ sagaName: SAGA_RESET_PICK_TOUR, isLoading: true }));
  try {
    const result = yield call(callResetPickTour, pickTour, configAuthValues);
    yield put(createToast({ type: 'success', message: i18n.t('RESET_PICK_TOUR_SUCCESS') }));
    yield put(resetActivePickTourComplete({ pickTour, pickTourIndex }));
    yield handleApiErrors(result?.data?.errors);
  } catch (error) {
    if ((error as any)?.response?.status === 500) {
      const resetPickTourError: APIError[] = [{ id: '', code: RESET_PICK_TOUR_ERROR, detail: JSON.stringify(error) }];
      yield handleApiErrors(resetPickTourError);
    } else {
      try {
        const url = `${PICKING_APIGEE_BASE_PATH}${BFF_V1}${BFF_PICK_TOURS}/${pickTour.pickTourID}/reassign?resetItems=false`;
        const method = 'POST';
        const config = configAuthValues;

        // Check if the error is PICKTOUR_ACTIVE or PICKTOUR_CLOSED
        const errors = (error as any)?.response?.data?.errors;
        const pickTourErrorDetails = [PICKTOUR_ACTIVE, PICKTOUR_CLOSED];
        const hasPickTourError = errors?.some(
          (err: APIError) => err.code === GENERIC_NON_SERVICE_ERROR && pickTourErrorDetails.includes(err.detail)
        );

        if (hasPickTourError) {
          // Refresh the orders list when pick tour is actively being picked or closed
          const storeID: string = yield select(selectPickerStoreID);
          yield put(getPickToursInProgress({ fulfillmentStoreID: storeID }));
        }

        yield catchApiErrors(error as any, { url, method, data: pickTour, config, saga: SAGA_RESET_PICK_TOUR });
      } catch (_error) {
        yield catchApiErrors(error as any);
      }
    }
  } finally {
    yield put(setLoading({ sagaName: SAGA_RESET_PICK_TOUR, isLoading: false }));
  }
}

export default function* watchResetPickTour() {
  yield takeEvery(getType(resetActivePickTour), runResetPickTourSaga);
}
